import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { useMilestones } from '../../contexts/MilestoneDataContext';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, serverTimestamp, getDoc, getDocs, addDoc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { Table, Badge, Space, Select, Card, Modal, Form, Input, Button, Tooltip, Tag, Typography, Spin, DatePicker } from 'antd';
import { CheckCircleOutlined, QuestionCircleOutlined, MedicineBoxOutlined, CloseCircleOutlined, EnvironmentOutlined, PhoneOutlined, MailOutlined, TeamOutlined, ClockCircleOutlined, ExclamationCircleOutlined, ReloadOutlined, SearchOutlined, ClearOutlined, PlusCircleOutlined, WarningOutlined, ShopOutlined, LockOutlined, QuestionOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
// Removed dashboard hooks that caused staff inheritance bug
import { useSiteMetrics } from '../../hooks/useDashboardMetrics';
import { useDetailedSiteData } from '../../hooks/useDetailedDashboardData';
import openaiService from '../../services/openaiService';
import { DIGITAL_PRODUCTS, getAvailableProducts } from '../../models/digitalProducts';
import crisisTaskTemplates from '../../data/crisisTaskTemplates.yaml';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

const StatusTab = ({ incidentId }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [incident, setIncident] = useState(null);
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(false);

  // 🎯 USE SHARED MILESTONE DATA  
  const { milestones: sharedMilestones, loading: milestonesLoading } = useMilestones({ incidentId });
  
  // Comprehensive incident data for AI analysis
  const [incidentData, setIncidentData] = useState({
    tasks: [],
    milestones: [],
    updates: [],
    staffStatus: [],
    incident: null
  });
  const [realTimeAnalysis, setRealTimeAnalysis] = useState(null);

  const [userOrgId, setUserOrgId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [staffModalVisible, setStaffModalVisible] = useState(false);
  
  // Manual Staff Add States
  const [addStaffModalVisible, setAddStaffModalVisible] = useState(false);
  const [addStaffType, setAddStaffType] = useState(null); // 'hospitalised' or 'deceased'
  const [liveUpdateModalVisible, setLiveUpdateModalVisible] = useState(false);
  const [deceasedStaffData, setDeceasedStaffData] = useState(null);
  const [form] = Form.useForm();
  const [addStaffForm] = Form.useForm();
  const [liveUpdateForm] = Form.useForm();

  // Badge modal states (simplified for incident-specific view)
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [selectedBadgeFilter, setSelectedBadgeFilter] = useState(null);
  const [badgeModalType, setBadgeModalType] = useState(null); // Only 'staff' used now

  // Site Status Modal (replicated from Dashboard)
  const [showSiteModal, setShowSiteModal] = useState(false);
  
  // Badge-specific modal state (replicated from Dashboard)
  const [selectedSiteBadgeFilter, setSelectedSiteBadgeFilter] = useState(null);
  const [showSiteBadgeModal, setShowSiteBadgeModal] = useState(false);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');

  // 🎯 INCIDENT-SPECIFIC STAFF METRICS (Fixed inheritance bug)
  // Calculate metrics from incident-specific staff data only
  const calculateIncidentStaffMetrics = () => {
    const metrics = {
      safe: 0,
      need_assistance: 0,
      unaccounted: 0,
      hospitalised: 0,
      deceased: 0
    };
    
    staff.forEach(staffMember => {
      const status = staffMember.status || 'unaccounted';
      if (metrics.hasOwnProperty(status)) {
        metrics[status]++;
      }
    });
    
    return metrics;
  };

  const incidentStaffMetrics = calculateIncidentStaffMetrics();

  // 🔧 SURGICAL FIX: Use real-time site metrics (same as Dashboard)
  const siteMetrics = useSiteMetrics(currentUser?.orgId);
  
  // Detailed site data for badge modal (replicated from Dashboard)
  const detailedSiteData = useDetailedSiteData(
    currentUser?.orgId, 
    selectedSiteBadgeFilter === 'all' ? null : selectedSiteBadgeFilter
  );

  // Professional Staff Status Icons - Clean and Corporate
  const statusOptions = [
    { 
      value: 'safe', 
      label: 'Safe', 
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />, 
      countColor: '#52c41a' 
    },
    { 
      value: 'need_assistance', 
      label: 'Need Help', 
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />, 
      countColor: '#ff4d4f' 
    },
    { 
      value: 'unaccounted', 
      label: 'Unaccounted', 
      icon: <QuestionCircleOutlined style={{ color: '#fa8c16' }} />, 
      countColor: '#fa8c16' 
    },
    { 
      value: 'hospitalised', 
      label: 'Hospitalised', 
      icon: <div style={{ position: 'relative', display: 'inline-block' }}>
        <MedicineBoxOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
        <PlusCircleOutlined style={{ color: '#1890ff', fontSize: '8px', position: 'absolute', top: '-2px', right: '-2px' }} />
      </div>, 
      countColor: '#1890ff' 
    },
    { 
      value: 'deceased', 
      label: 'Deceased', 
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />, 
      countColor: '#ff4d4f' 
    }
  ];

  // Professional Site Status Icons - Clean and Corporate
  const siteStatusOptions = [
    { 
      value: 'active', 
      label: 'Active', 
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />, 
      countColor: '#52c41a' 
    },
    { 
      value: 'degraded', 
      label: 'Degraded', 
      icon: <WarningOutlined style={{ color: '#fa8c16' }} />, 
      countColor: '#fa8c16' 
    },
    { 
      value: 'evacuating', 
      label: 'Evacuating', 
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />, 
      countColor: '#ff4d4f' 
    },
    { 
      value: 'evacuated', 
      label: 'Evacuated', 
      icon: <EnvironmentOutlined style={{ color: '#722ed1' }} />, 
      countColor: '#722ed1' 
    },
    { 
      value: 'closed_and_secure', 
      label: 'Closed and Secure', 
      icon: <LockOutlined style={{ color: '#1890ff' }} />, 
      countColor: '#1890ff' 
    },
    { 
      value: 'unknown', 
      label: 'Unknown', 
      icon: <QuestionOutlined style={{ color: '#8c8c8c' }} />, 
      countColor: '#8c8c8c' 
    }
  ];

  useEffect(() => {
    // Get user's orgId
    const fetchUserOrgId = async () => {
      try {
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserOrgId(userDoc.data().orgId);
        }
      } catch (error) {
        console.error('Error fetching user orgId:', error);
      }
    };
    fetchUserOrgId();
  }, [currentUser.uid, db]);

  useEffect(() => {
    // Load incident details
    const fetchIncident = async () => {
      try {
        const incidentDoc = await getDoc(doc(db, 'c3_incidents', incidentId));
        if (incidentDoc.exists()) {
          const incidentData = { id: incidentDoc.id, ...incidentDoc.data() };
          setIncident(incidentData);
        }
      } catch (error) {
        console.error('Error fetching incident:', error);
      }
    };
    
    if (incidentId) {
      fetchIncident();
    }
  }, [incidentId, db]);

  useEffect(() => {
    if (!userOrgId) return;

    const staffRef = collection(db, 'c3_staff_status');
    const q = query(
      staffRef,
      where('orgId', '==', userOrgId),
      orderBy('lastUpdated', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const staffList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStaff(staffList);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching staff status:', error);
        setError('Failed to load staff status');
        setLoading(false);
        toast.error('Failed to load staff status');
      }
    );

    return () => unsubscribe();
  }, [userOrgId, db]);

  // COMPREHENSIVE DATA LOADING FOR AI ANALYSIS - ENHANCED TO INCLUDE ALL CRISIS DATA + DIGITAL PRODUCTS + TASK TEMPLATES
  useEffect(() => {
    if (!incidentId || !userOrgId || !incident) return;

    const loadComprehensiveData = async () => {
      try {
        console.log('🔄 Loading COMPREHENSIVE incident data with Digital Products for AI analysis...');
        
        let tasks = [];
        let milestones = [];
        let updates = [];
        let communications = [];
        let liveChatMessages = [];
        let documentActivities = [];
        let mediaIntelligence = [];
        let automationEvents = [];
        let siteStatusUpdates = [];
        let regulatoryActivities = [];
        let contactDirectoryUpdates = [];
        let timelineEvents = [];
        let systemEvents = [];
        let digitalProducts = [];
        let organizationProducts = [];
        let availableCrisisTemplates = [];

        // 1. TASKS - Core response activities
        try {
          const tasksQuery = query(
            collection(db, 'c3_tasks'),
      where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId)
          );
          const tasksSnapshot = await getDocs(tasksQuery);
          tasks = tasksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Tasks loaded:', tasks.length);
        } catch (taskError) {
          console.warn('⚠️ Could not load tasks (permission issue):', taskError.message);
          tasks = [];
        }

        // 2. MILESTONES - Use shared milestone data (no separate query needed)
        milestones = sharedMilestones || [];
        console.log('✅ Milestones loaded from shared data:', milestones.length);

        // 3. ACTIVITY LOGS - General incident activities
        try {
          const logsQuery = query(
            collection(db, 'c3_activity_logs'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const logsSnapshot = await getDocs(logsQuery);
          updates = logsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Activity logs loaded:', updates.length);
        } catch (logError) {
          console.warn('⚠️ Could not load activity logs (permission issue):', logError.message);
          updates = [];
        }

        // 4. COMMUNICATIONS - SMS, bulk messages, mass communications
        try {
          const commsQuery = query(
            collection(db, 'c3_communications'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('sentAt', 'desc')
          );
          const commsSnapshot = await getDocs(commsQuery);
          communications = commsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Communications loaded:', communications.length);
        } catch (commsError) {
          console.warn('⚠️ Could not load communications:', commsError.message);
          communications = [];
        }

        // 5. LIVE CHAT MESSAGES - Real-time incident chat
        try {
          const chatQuery = query(
            collection(db, 'c3_incident_chat'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const chatSnapshot = await getDocs(chatQuery);
          liveChatMessages = chatSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Live chat messages loaded:', liveChatMessages.length);
        } catch (chatError) {
          console.warn('⚠️ Could not load live chat messages:', chatError.message);
          liveChatMessages = [];
        }

        // 6. DOCUMENT ACTIVITIES - Document uploads, edits, collaboration
        try {
          const docsQuery = query(
            collection(db, 'c3_document_activities'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const docsSnapshot = await getDocs(docsQuery);
          documentActivities = docsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Document activities loaded:', documentActivities.length);
        } catch (docsError) {
          console.warn('⚠️ Could not load document activities:', docsError.message);
          documentActivities = [];
        }

        // 7. MEDIA INTELLIGENCE - News monitoring, social media
        try {
          const mediaQuery = query(
            collection(db, 'c3_media_intelligence'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const mediaSnapshot = await getDocs(mediaQuery);
          mediaIntelligence = mediaSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Media intelligence loaded:', mediaIntelligence.length);
        } catch (mediaError) {
          console.warn('⚠️ Could not load media intelligence:', mediaError.message);
          mediaIntelligence = [];
        }

        // 8. AUTOMATION EVENTS - Smart alerts, automated responses
        try {
          const automationQuery = query(
            collection(db, 'c3_automation_events'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const automationSnapshot = await getDocs(automationQuery);
          automationEvents = automationSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Automation events loaded:', automationEvents.length);
        } catch (automationError) {
          console.warn('⚠️ Could not load automation events:', automationError.message);
          automationEvents = [];
        }

        // 9. SITE STATUS UPDATES - Infrastructure status changes
        try {
          const siteQuery = query(
            collection(db, 'c3_site_status_updates'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const siteSnapshot = await getDocs(siteQuery);
          siteStatusUpdates = siteSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Site status updates loaded:', siteStatusUpdates.length);
        } catch (siteError) {
          console.warn('⚠️ Could not load site status updates:', siteError.message);
          siteStatusUpdates = [];
        }

        // 9b. ORGANIZATION SITES - For AI site detection and location intelligence
        let organizationSites = [];
        try {
          const orgSitesQuery = query(
            collection(db, 'c3_sites'),
            where('organization_id', '==', userOrgId),
            orderBy('site_name', 'asc')
          );
          const orgSitesSnapshot = await getDocs(orgSitesQuery);
          organizationSites = orgSitesSnapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data(),
            // Standardize address field for matching
            address: doc.data().address || doc.data().location || doc.data().site_address || '',
            name: doc.data().site_name || doc.data().name || 'Unknown Site',
            type: doc.data().site_type || doc.data().type || 'facility'
          }));
          console.log('✅ Organization sites loaded for AI detection:', organizationSites.length);
        } catch (orgSitesError) {
          console.warn('⚠️ Could not load organization sites:', orgSitesError.message);
          organizationSites = [];
        }

        // 10. REGULATORY COMPLIANCE - Legal obligations, triggers
        try {
          const regulatoryQuery = query(
            collection(db, 'c3_regulatory_activities'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const regulatorySnapshot = await getDocs(regulatoryQuery);
          regulatoryActivities = regulatorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Regulatory activities loaded:', regulatoryActivities.length);
        } catch (regulatoryError) {
          console.warn('⚠️ Could not load regulatory activities:', regulatoryError.message);
          regulatoryActivities = [];
        }

        // 11. CONTACT DIRECTORY UPDATES - Staff contact changes
        try {
          const contactQuery = query(
            collection(db, 'c3_contact_updates'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const contactSnapshot = await getDocs(contactQuery);
          contactDirectoryUpdates = contactSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Contact directory updates loaded:', contactDirectoryUpdates.length);
        } catch (contactError) {
          console.warn('⚠️ Could not load contact directory updates:', contactError.message);
          contactDirectoryUpdates = [];
        }

        // 12. TIMELINE EVENTS - Major incident timeline entries
        try {
          const timelineQuery = query(
            collection(db, 'c3_timeline_events'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const timelineSnapshot = await getDocs(timelineQuery);
          timelineEvents = timelineSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ Timeline events loaded:', timelineEvents.length);
        } catch (timelineError) {
          console.warn('⚠️ Could not load timeline events:', timelineError.message);
          timelineEvents = [];
        }

        // 13. SYSTEM EVENTS - Technical alerts, system status
        try {
          const systemQuery = query(
            collection(db, 'c3_system_events'),
            where('incidentId', '==', incidentId),
            where('orgId', '==', userOrgId),
            orderBy('timestamp', 'desc')
          );
          const systemSnapshot = await getDocs(systemQuery);
          systemEvents = systemSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('✅ System events loaded:', systemEvents.length);
        } catch (systemError) {
          console.warn('⚠️ Could not load system events:', systemError.message);
          systemEvents = [];
        }

        // 14. ORGANIZATION'S DIGITAL PRODUCTS - Available crisis response resources
        try {
          const orgDoc = await getDoc(doc(db, 'c3_organisations', userOrgId));
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const subscriptionTier = orgData.subscriptionTier || 'business_pro';
            
            // Get available digital products based on subscription
            digitalProducts = getAvailableProducts(subscriptionTier);
            console.log('✅ Available digital products loaded:', digitalProducts.length, `(${subscriptionTier})`);

            // Get organization's custom products if they have any
            const orgProductsQuery = query(
              collection(db, 'c3_organisation_products'),
              where('organisationId', '==', userOrgId)
            );
            const orgProductsSnapshot = await getDocs(orgProductsQuery);
            organizationProducts = orgProductsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log('✅ Organization products loaded:', organizationProducts.length);
          }
        } catch (productsError) {
          console.warn('⚠️ Could not load digital products:', productsError.message);
          digitalProducts = [];
          organizationProducts = [];
        }

        // 15. CRISIS TASK TEMPLATES - Pre-defined response actions for this incident type
        try {
          const incidentType = incident?.crisisType?.toLowerCase() || 'critical_incident';
          
          // Load crisis task templates for this incident type
          if (crisisTaskTemplates?.crisis_templates?.[incidentType]) {
            availableCrisisTemplates = crisisTaskTemplates.crisis_templates[incidentType];
            console.log('✅ Crisis task templates loaded for type:', incidentType);
          } else {
            // Fallback to critical_incident template
            availableCrisisTemplates = crisisTaskTemplates?.crisis_templates?.critical_incident || {};
            console.log('✅ Using fallback crisis task templates (critical_incident)');
          }
        } catch (templatesError) {
          console.warn('⚠️ Could not load crisis task templates:', templatesError.message);
          availableCrisisTemplates = {};
        }

        // COMPREHENSIVE DATA PACKAGE FOR AI INCLUDING DIGITAL PRODUCTS & TEMPLATES
        const comprehensiveData = {
          // Core crisis management data
          tasks,
          milestones,
          updates,
          staffStatus: staff,
          incident,
          
          // Communication and collaboration data
          communications,
          liveChatMessages,
          documentActivities,
          
          // Intelligence and monitoring data
          mediaIntelligence,
          automationEvents,
          siteStatusUpdates,
          organizationSites, // For AI site detection and location intelligence
          
          // Compliance and administration data
          regulatoryActivities,
          contactDirectoryUpdates,
          timelineEvents,
          systemEvents,

          // CRISIS COMPASS DIGITAL PRODUCTS & RESOURCES FOR AI RECOMMENDATIONS
          digitalProducts,
          organizationProducts,
          availableCrisisTemplates,
          allDigitalProducts: DIGITAL_PRODUCTS // Full catalog for AI reference
        };

        setIncidentData(comprehensiveData);

        // Perform comprehensive real-time analysis
        const analysis = performComprehensiveRealTimeAnalysis(comprehensiveData);
        setRealTimeAnalysis(analysis);

        console.log('✅ COMPREHENSIVE crisis data loaded with ALL data sources + Digital Products:', {
          tasks: tasks.length,
          milestones: milestones.length,
          updates: updates.length,
          communications: communications.length,
          liveChatMessages: liveChatMessages.length,
          documentActivities: documentActivities.length,
          mediaIntelligence: mediaIntelligence.length,
          automationEvents: automationEvents.length,
          siteStatusUpdates: siteStatusUpdates.length,
          regulatoryActivities: regulatoryActivities.length,
          contactDirectoryUpdates: contactDirectoryUpdates.length,
          timelineEvents: timelineEvents.length,
          systemEvents: systemEvents.length,
          digitalProducts: digitalProducts.length,
          organizationProducts: organizationProducts.length,
          availableCrisisTemplates: Object.keys(availableCrisisTemplates).length
        });

      } catch (error) {
        console.error('❌ Error loading comprehensive data:', error);
        
        // Even if everything fails, set basic data so summary can work
        const basicData = {
          tasks: [],
          milestones: [],
          updates: [],
          staffStatus: staff,
          incident,
          communications: [],
          liveChatMessages: [],
          documentActivities: [],
          mediaIntelligence: [],
          automationEvents: [],
          siteStatusUpdates: [],
          regulatoryActivities: [],
          contactDirectoryUpdates: [],
          timelineEvents: [],
          systemEvents: [],
          digitalProducts: [],
          organizationProducts: [],
          availableCrisisTemplates: {},
          allDigitalProducts: DIGITAL_PRODUCTS
        };
        
        setIncidentData(basicData);
        
        // Perform analysis with just staff and incident data
        const basicAnalysis = performComprehensiveRealTimeAnalysis(basicData);
        setRealTimeAnalysis(basicAnalysis);
        
        console.log('✅ Basic data fallback complete');
      }
    };

    loadComprehensiveData();
  }, [incidentId, userOrgId, incident, staff, db]);

  // COMPREHENSIVE REAL-TIME ANALYSIS FUNCTION
  const performComprehensiveRealTimeAnalysis = useCallback((data) => {
    const now = new Date();
    const { 
      tasks, milestones, updates, staffStatus, incident,
      communications, liveChatMessages, documentActivities,
      mediaIntelligence, automationEvents, siteStatusUpdates, organizationSites = [],
      regulatoryActivities, contactDirectoryUpdates, timelineEvents, systemEvents,
      digitalProducts = [], organizationProducts = [], availableCrisisTemplates = {}, allDigitalProducts = {}
    } = data;

    // Task analysis
    const completedTasks = tasks.filter(t => t.status === 'completed');
    const overdueTasks = tasks.filter(t => {
      if (!t.dueDate) return false;
      const dueDate = t.dueDate.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
      return dueDate < now && t.status !== 'completed';
    });
    const taskCompletionRate = tasks.length > 0 ? (completedTasks.length / tasks.length) * 100 : 0;

    // Milestone analysis
    const completedMilestones = milestones.filter(m => m.status === 'Complete');
    const milestoneCompletionRate = milestones.length > 0 ? (completedMilestones.length / milestones.length) * 100 : 0;

    // Staff analysis
    const safeStaff = staffStatus.filter(s => s.status === 'safe');
    const needHelpStaff = staffStatus.filter(s => s.status === 'need_assistance');
    const unaccountedStaff = staffStatus.filter(s => s.status === 'unaccounted');
    const hospitalisedStaff = staffStatus.filter(s => s.status === 'hospitalised');
    const deceasedStaff = staffStatus.filter(s => s.status === 'deceased');
    
    const staffSafetyRate = staffStatus.length > 0 ? (safeStaff.length / staffStatus.length) * 100 : 100;

    // Incident age
    const incidentStart = incident?.occurredAt?.toDate ? incident.occurredAt.toDate() : 
                         incident?.createdAt?.toDate ? incident.createdAt.toDate() : now;
    const incidentAge = Math.floor((now - incidentStart) / (1000 * 60 * 60)); // hours

    // Recent activity analysis (last 6 hours)
    const sixHoursAgo = new Date(now.getTime() - (6 * 60 * 60 * 1000));
    const recentUpdates = updates.filter(u => {
      const updateTime = u.timestamp?.toDate ? u.timestamp.toDate() : new Date(u.timestamp);
      return updateTime > sixHoursAgo;
    });

    // Communication analysis
    const recentCommunications = communications.filter(c => {
      const sentTime = c.sentAt?.toDate ? c.sentAt.toDate() : new Date(c.sentAt);
      return sentTime > sixHoursAgo;
    });

    // Live chat analysis
    const recentChatMessages = liveChatMessages.filter(m => {
      const msgTime = m.timestamp?.toDate ? m.timestamp.toDate() : new Date(m.timestamp);
      return msgTime > sixHoursAgo;
    });

    // Document activity analysis
    const recentDocumentActivity = documentActivities.filter(d => {
      const docTime = d.timestamp?.toDate ? d.timestamp.toDate() : new Date(d.timestamp);
      return docTime > sixHoursAgo;
    });

    // Media intelligence analysis
    const recentMediaMentions = mediaIntelligence.filter(m => {
      const mediaTime = m.timestamp?.toDate ? m.timestamp.toDate() : new Date(m.timestamp);
      return mediaTime > sixHoursAgo;
    });

    // Automation events analysis
    const recentAutomationEvents = automationEvents.filter(a => {
      const autoTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      return autoTime > sixHoursAgo;
    });

    // Site status analysis
    const recentSiteUpdates = siteStatusUpdates.filter(s => {
      const siteTime = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      return siteTime > sixHoursAgo;
    });

    // Regulatory compliance analysis
    const recentRegulatoryActivity = regulatoryActivities.filter(r => {
      const regTime = r.timestamp?.toDate ? r.timestamp.toDate() : new Date(r.timestamp);
      return regTime > sixHoursAgo;
    });

    // Timeline events analysis
    const recentTimelineEvents = timelineEvents.filter(t => {
      const timelineTime = t.timestamp?.toDate ? t.timestamp.toDate() : new Date(t.timestamp);
      return timelineTime > sixHoursAgo;
    });

    // System events analysis
    const recentSystemEvents = systemEvents.filter(s => {
      const sysTime = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      return sysTime > sixHoursAgo;
    });

    // Digital Products analysis
    const availableDigitalProductsCount = digitalProducts.length;
    const organizationProductsCount = organizationProducts.length;
    const availableCrisisTemplatesCount = Object.keys(availableCrisisTemplates).length;

    // Calculate total activity across ALL data sources
    const totalRecentActivity = recentUpdates.length + recentCommunications.length + 
                               recentChatMessages.length + recentDocumentActivity.length +
                               recentMediaMentions.length + recentAutomationEvents.length +
                               recentSiteUpdates.length + recentRegulatoryActivity.length +
                               recentTimelineEvents.length + recentSystemEvents.length;

    return {
      // Original metrics
      taskCompletionRate,
      milestoneCompletionRate,
      staffSafetyRate,
      overdueTasks: overdueTasks.length,
      totalTasks: tasks.length,
      totalMilestones: milestones.length,
      safeStaff: safeStaff.length,
      needHelpStaff: needHelpStaff.length,
      unaccountedStaff: unaccountedStaff.length,
      hospitalisedStaff: hospitalisedStaff.length,
      deceasedStaff: deceasedStaff.length,
      totalStaff: staffStatus.length,
      incidentAge,
      recentActivity: totalRecentActivity,
      
      // Enhanced metrics for comprehensive analysis
      communicationMetrics: {
        total: communications.length,
        recent: recentCommunications.length,
        types: [...new Set(communications.map(c => c.type || 'unknown'))]
      },
      
      chatMetrics: {
        total: liveChatMessages.length,
        recent: recentChatMessages.length,
        participants: [...new Set(liveChatMessages.map(m => m.userId || m.userName))]
      },
      
      documentMetrics: {
        total: documentActivities.length,
        recent: recentDocumentActivity.length,
        types: [...new Set(documentActivities.map(d => d.activityType || 'unknown'))]
      },
      
      mediaMetrics: {
        total: mediaIntelligence.length,
        recent: recentMediaMentions.length,
        sources: [...new Set(mediaIntelligence.map(m => m.source || 'unknown'))]
      },
      
      automationMetrics: {
        total: automationEvents.length,
        recent: recentAutomationEvents.length,
        types: [...new Set(automationEvents.map(a => a.eventType || 'unknown'))]
      },
      
      siteMetrics: {
        total: siteStatusUpdates.length,
        recent: recentSiteUpdates.length,
        statuses: [...new Set(siteStatusUpdates.map(s => s.status || 'unknown'))],
        sites: organizationSites // For AI site detection and location intelligence
      },
      
      regulatoryMetrics: {
        total: regulatoryActivities.length,
        recent: recentRegulatoryActivity.length,
        types: [...new Set(regulatoryActivities.map(r => r.activityType || 'unknown'))]
      },
      
      timelineMetrics: {
        total: timelineEvents.length,
        recent: recentTimelineEvents.length,
        eventTypes: [...new Set(timelineEvents.map(t => t.eventType || 'unknown'))]
      },
      
      systemMetrics: {
        total: systemEvents.length,
        recent: recentSystemEvents.length,
        severity: [...new Set(systemEvents.map(s => s.severity || 'unknown'))]
      },

      digitalProductMetrics: {
        total: availableDigitalProductsCount,
        organization: organizationProductsCount,
        templates: availableCrisisTemplatesCount
      },
      
      lastCalculated: now
    };
  }, []);

  // AI EXECUTIVE SUMMARY GENERATION
  useEffect(() => {
    console.log('🔄 Executive Summary Effect Triggered', {
      realTimeAnalysis: !!realTimeAnalysis,
      incident: !!incidentData.incident,
      tasksLength: incidentData.tasks.length,
      staffLength: incidentData.staffStatus.length
    });

    const generateAIExecutiveSummary = async () => {
      if (!realTimeAnalysis || !incidentData.incident) {
        console.log('⚠️ Missing analysis data, using basic summary only');
        generateEnhancedFallbackSummary();
        return;
      }

      try {
        setSummaryLoading(true);
        console.log('🤖 Attempting AI Executive Summary generation...');
        console.log('🔍 Incident data:', incidentData.incident);
        console.log('🔍 Live chat messages:', incidentData.liveChatMessages);

        // ALWAYS try OpenAI service - no fallback unless it truly fails
        if (openaiService && openaiService.generateExecutiveSummary) {
          try {
            console.log('🚀 Calling Cloud Function...');
            const aiSummary = await openaiService.generateExecutiveSummary(
              incidentData,
              realTimeAnalysis,
              db,
              currentUser
            );

            console.log('📄 Cloud Function response:', aiSummary);

            if (aiSummary && aiSummary.summary) {
              setExecutiveSummary(aiSummary.summary);
              console.log('✅ AI Executive Summary generated successfully');
              return;
            } else {
              console.log('❌ Cloud Function returned empty summary');
            }
          } catch (aiError) {
            console.log('⚠️ OpenAI service failed, using enhanced fallback:', aiError.message);
          }
        } else {
          console.log('❌ OpenAI service not available');
        }

        // Enhanced fallback with real data
        console.log('🔧 Using enhanced fallback summary');
        generateEnhancedFallbackSummary();

    } catch (error) {
        console.error('❌ Error in summary generation:', error);
        generateEnhancedFallbackSummary();
      } finally {
        setSummaryLoading(false);
      }
    };

    const generateEnhancedFallbackSummary = () => {
      const { incident } = incidentData;
      const analysis = realTimeAnalysis;
      const now = new Date();
      
      console.log('🔧 Generating enhanced fallback summary with analysis:', analysis);
      
      // Generate structured HTML summary per briefing specification
      let summaryHtml = '';

      // Italicized timestamp header
      summaryHtml += `<p style="font-style: italic; color: #000; margin-bottom: 16px;">This is an AI-generated crisis summary, drawing on all available crisis information, live chat inputs from the CMT and news/media intelligence. Generated ${now.toLocaleString()}</p>`;

      // Extract live chat context for WHAT section
      const liveMessages = incidentData?.liveChatMessages || [];
      const recentMessages = liveMessages.slice(-5); // Get last 5 messages for context
      let liveContext = '';
      if (recentMessages.length > 0) {
        const contextSnippets = recentMessages.map(msg => msg.message).join('. ');
        liveContext = ` Live updates include: ${contextSnippets}.`;
      }

      // WHAT section with live context
      summaryHtml += `<p><strong>WHAT:</strong> ${incident?.crisisType || 'Crisis'} incident affecting ${incident?.title || 'the organization'}. Current status: ${incident?.status?.toUpperCase() || 'ACTIVE'}. Severity level: ${incident?.severity?.toUpperCase() || 'STANDARD'}.${liveContext} This incident has been active for ${analysis?.incidentAge || 0} hours with ${analysis?.totalTasks || 0} response tasks assigned (${(analysis?.taskCompletionRate || 0).toFixed(0)}% complete).</p>`;

      summaryHtml += `<br>`;

      // WHERE section  
      summaryHtml += `<p><strong>WHERE:</strong> ${incident?.location || 'Multiple locations'}. ${analysis?.siteMetrics?.total || 0} sites are being monitored with ${analysis?.siteMetrics?.recent || 0} recent status updates. Infrastructure impact across operational areas with varying degrees of disruption.</p>`;

      summaryHtml += `<br>`;

      // WHEN section
      const activationTime = incident?.occurredAt?.toDate ? incident.occurredAt.toDate() : 
                            incident?.createdAt?.toDate ? incident.createdAt.toDate() : now;
      summaryHtml += `<p><strong>WHEN:</strong> Incident activated at ${activationTime.toLocaleString()}. Duration: ${analysis?.incidentAge || 0} hours. Last updated by ${incident?.lastUpdatedBy || 'System'}. Recent activity: ${analysis?.recentActivity || 0} updates in the past 6 hours across all monitoring systems.</p>`;

      summaryHtml += `<br>`;

      // CAUSE section
      summaryHtml += `<p><strong>CAUSE:</strong> ${incident?.description || 'Incident cause under investigation'}. Root cause analysis ongoing. Contributing factors being assessed through comprehensive data collection across multiple data sources.</p>`;

      summaryHtml += `<br>`;

      // IMPACT section
      summaryHtml += `<p><strong>IMPACT:</strong> ${analysis?.totalStaff || 0} staff members tracked with ${(analysis?.staffSafetyRate || 0).toFixed(0)}% confirmed safe. ${analysis?.overdueTasks || 0} critical tasks overdue. ${analysis?.communicationMetrics?.recent || 0} emergency communications sent.</p>`;

      summaryHtml += `<br>`;

      // RESPONSE section
      summaryHtml += `<p><strong>RESPONSE:</strong> Crisis management team activated with ${analysis?.totalMilestones || 0} milestones established (${(analysis?.milestoneCompletionRate || 0).toFixed(0)}% achieved). ${analysis?.documentMetrics?.recent || 0} critical documents processed. ${analysis?.automationMetrics?.recent || 0} automated response protocols triggered.</p>`;

      summaryHtml += `<br>`;

      // RECOMMENDATION/S section - SHORT AND SHARP
      let recommendations = [];
      if (analysis?.staffSafetyRate < 95) {
        recommendations.push('Verify staff safety immediately');
      }
      if (analysis?.taskCompletionRate < 70) {
        recommendations.push('Escalate task completion');
      }
      if (analysis?.overdueTasks > 0) {
        recommendations.push(`Complete ${analysis?.overdueTasks} overdue tasks`);
      }
      if (recommendations.length === 0) {
        recommendations.push('Maintain current protocols');
      }
      summaryHtml += `<p><strong>RECOMMENDATION/S:</strong> ${recommendations.join('. ')}.</p>`;

      summaryHtml += `<br>`;

      // IMMEDIATE ACTION/S REQUIRED section
      let immediateActions = [];
      if (analysis?.unaccountedStaff > 0) {
        immediateActions.push(`Locate ${analysis?.unaccountedStaff} unaccounted staff`);
      }
      if (analysis?.needHelpStaff > 0) {
        immediateActions.push(`Assist ${analysis?.needHelpStaff} staff requiring help`);
      }
      if (analysis?.overdueTasks > 0) {
        immediateActions.push(`Complete ${analysis?.overdueTasks} overdue tasks`);
      }
      
      if (immediateActions.length === 0) {
        immediateActions.push('Monitor situation and maintain operational readiness');
      }
      summaryHtml += `<p><strong>IMMEDIATE ACTION/S REQUIRED:</strong> ${immediateActions.slice(0, 5).join('. ')}.</p>`;

      summaryHtml += `<br>`;

      // Staff impact summary section
      summaryHtml += `<p><strong>STAFF IMPACT SUMMARY:</strong> Total staff tracked: ${analysis?.totalStaff || 0}. Safe: ${analysis?.safeStaff || 0} (${(analysis?.staffSafetyRate || 0).toFixed(0)}%)`;
      if (analysis?.needHelpStaff > 0) summaryHtml += `, Need assistance: ${analysis?.needHelpStaff}`;
      if (analysis?.unaccountedStaff > 0) summaryHtml += `, Unaccounted: ${analysis?.unaccountedStaff}`;
      if (analysis?.hospitalisedStaff > 0) summaryHtml += `, Hospitalized: ${analysis?.hospitalisedStaff}`;
      if (analysis?.deceasedStaff > 0) summaryHtml += `, Deceased: ${analysis?.deceasedStaff}`;
      summaryHtml += `. Staff safety status: ${(analysis?.staffSafetyRate || 0) >= 95 ? 'EXCELLENT' : (analysis?.staffSafetyRate || 0) >= 80 ? 'GOOD' : 'REQUIRES ATTENTION'}.</p>`;

      summaryHtml += `<br>`;

      // Site status summary section
      summaryHtml += `<p><strong>SITE STATUS SUMMARY:</strong> ${analysis?.siteMetrics?.total || 0} sites monitored with ${analysis?.siteMetrics?.recent || 0} recent updates. Site statuses: ${analysis?.siteMetrics?.statuses?.join(', ') || 'Status pending'}. Infrastructure impact varies by location with ongoing assessment of operational capacity and recovery requirements.</p>`;
      
      setExecutiveSummary(summaryHtml);
      console.log('✅ Enhanced fallback summary generated with structured HTML format');
    };

    // Always try to generate summary if we have incident data
    if (incident) {
      const timeoutId = setTimeout(generateAIExecutiveSummary, 500);
      return () => clearTimeout(timeoutId);
        } else {
      console.log('⚠️ No incident data available for summary - showing loading state');
      setSummaryLoading(true);
      setExecutiveSummary('');
    }
  }, [realTimeAnalysis, incidentData, incident, currentUser, db]);

  const handleRefreshSummary = () => {
    if (realTimeAnalysis && incidentData.incident) {
      setSummaryLoading(true);
      // Trigger regeneration
      const newAnalysis = performComprehensiveRealTimeAnalysis(incidentData);
      setRealTimeAnalysis(newAnalysis);
    }
  };

  const handleStatusChange = async (staffId, newStatus) => {
    try {
      console.log('🔄 Updating staff status:', { staffId, newStatus });
      
      const staffDocRef = doc(db, 'c3_staff_status', staffId);
      
      // Check if document exists first
      const staffDoc = await getDoc(staffDocRef);
      
      if (staffDoc.exists()) {
        // Update existing document
        await updateDoc(staffDocRef, {
        status: newStatus,
        lastUpdated: serverTimestamp(),
          statusChangedBy: currentUser.uid,
          statusChangedByName: currentUser.displayName || currentUser.email
        });
        console.log('✅ Staff status updated successfully');
        } else {
        // Create new document if it doesn't exist
        await setDoc(staffDocRef, {
          id: staffId,
          status: newStatus,
          incidentId: incidentId,
          orgId: userOrgId,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          statusChangedBy: currentUser.uid,
          statusChangedByName: currentUser.displayName || currentUser.email
        });
        console.log('✅ Staff status document created successfully');
      }
      
      // Log activity
      await addDoc(collection(db, 'c3_activity_logs'), {
        incidentId: incidentId,
        orgId: userOrgId,
        type: 'STAFF_STATUS_CHANGED',
        details: `Staff status changed to ${newStatus}`,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        timestamp: serverTimestamp(),
        metadata: {
          staffId: staffId,
          newStatus: newStatus
        }
      });

      toast.success('Staff status updated successfully');
    } catch (error) {
      console.error('❌ Error updating staff status:', error);
      toast.error(`Failed to update staff status: ${error.message}`);
    }
  };

  // Site status updates removed - focusing on incident-specific staff data

  const handleLocationUpdate = async (values) => {
    try {
      const staffDocRef = doc(db, 'c3_staff_status', selectedStaff.id);
      await updateDoc(staffDocRef, {
        location: values.location,
        notes: values.notes,
        lastUpdated: serverTimestamp()
      });
      toast.success('Location updated successfully');
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    }
  };

  // Manual Staff Add Functions
  const handleAddStaffClick = (statusType) => {
    setAddStaffType(statusType);
    setAddStaffModalVisible(true);
  };

  const handleAddStaffSubmit = async (values) => {
    try {
      const staffPayload = {
        fullName: values.fullName,
        email: values.email || null,
        phone: values.phone || null,
        staffId: values.staffId || null,
        location: values.location,
        incidentRole: values.incidentRole || null,
        status: addStaffType,
        statusChangeTime: values.statusChangeTime.toDate(),
        notes: values.notes || '',
        source: 'manual'
      };

      // For deceased: show confirmation popup first with custom message
      if (addStaffType === 'deceased') {
        setDeceasedStaffData(staffPayload);
        setLiveUpdateModalVisible(true);
        setAddStaffModalVisible(false);
        return;
      }

      // For non-deceased: call server directly
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions(undefined, 'australia-southeast2');
      const callAdd = httpsCallable(functions, 'addStaffMemberAndAnnounce');
      const identity = staffPayload.staffId || staffPayload.fullName || 'unknown';
      const dateStr = new Date().toISOString().slice(0,10);
      const dedupeKey = `${incidentId}:${identity}:${staffPayload.status}:${dateStr}`;

      const resp = await callAdd({
        orgId: userOrgId,
        incidentId,
        cmtId: null,
        staffPayload,
        messageText: '',
        postToChat: false,
        dedupeKey
      });

      const result = resp?.data || {};
      toast.success(result?.deduped ? 'Already recorded' : `Staff member added to ${addStaffType} status successfully`);
      setAddStaffModalVisible(false);
      addStaffForm.resetFields();

    } catch (error) {
      console.error('Error adding staff member:', error);
      toast.error('Failed to add staff member');
    }
  };

  const handleLiveUpdateSubmit = async (values) => {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions(undefined, 'australia-southeast2');
      const callAdd = httpsCallable(functions, 'addStaffMemberAndAnnounce');
      const identity = deceasedStaffData?.staffId || deceasedStaffData?.fullName || 'unknown';
      const dateStr = new Date().toISOString().slice(0,10);
      const dedupeKey = `${incidentId}:${identity}:deceased:${dateStr}`;
      const resp = await callAdd({
        orgId: userOrgId,
        incidentId,
        cmtId: null,
        staffPayload: { ...deceasedStaffData, status: 'deceased' },
        messageText: values.liveUpdateMessage,
        postToChat: true,
        dedupeKey
      });

      const result = resp?.data || {};
      toast.success(result?.deduped ? 'Already recorded' : 'Deceased staff recorded and announced');
      setLiveUpdateModalVisible(false);
      liveUpdateForm.resetFields();
      setDeceasedStaffData(null);
      setAddStaffModalVisible(false);
      addStaffForm.resetFields();
      
    } catch (error) {
      console.error('Error posting live update:', error);
      toast.error('Failed to post live update');
    }
  };

  // Dashboard-style badge click handlers
  const handleBadgeClick = (filterValue, modalType) => {
    if (modalType === 'site') {
      // Handle site status modal (replicated from Dashboard)
      setShowSiteModal(true);
    } else {
      // Handle staff badge modal
      setSelectedBadgeFilter(filterValue);
      setBadgeModalType(modalType);
      setShowBadgeModal(true);
    }
  };

  // Site badge click handler (replicated from Dashboard)
  const handleSiteBadgeClick = (e, filterValue) => {
    e.stopPropagation(); // Prevent card click
    setSelectedSiteBadgeFilter(filterValue);
    setShowSiteBadgeModal(true);
  };

  // Clear site search
  const clearSiteSearch = () => {
    setSiteSearchTerm('');
  };

  // Site click handlers removed - focusing on incident-specific staff data

  // Format summary HTML content (replacing markdown-based function)
  const formatSummaryHtml = (htmlContent) => {
    // Directly render HTML content from structured AI summary
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        style={{ 
          fontSize: '16px', 
          lineHeight: '1.6'
        }}
      />
    );
  };

  // Auto-refresh every 10 minutes
  useEffect(() => {
    if (!realTimeAnalysis || !incidentData.incident) return;

    const autoRefreshInterval = setInterval(() => {
      console.log('🕐 Auto-refreshing executive summary (10-minute interval)');
      if (realTimeAnalysis && incidentData.incident) {
        setSummaryLoading(true);
        const newAnalysis = performComprehensiveRealTimeAnalysis(incidentData);
        setRealTimeAnalysis(newAnalysis);
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(autoRefreshInterval);
  }, [realTimeAnalysis, incidentData, performComprehensiveRealTimeAnalysis]);

  const staffColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {record.team && (
            <Tooltip title={`Team: ${record.team}`}>
              <Tag icon={<TeamOutlined />} color="blue">
                {record.team}
              </Tag>
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role'
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {(record.contactInfo || record.phoneNumber || record.phone) && (
            <Space>
              <PhoneOutlined />
              <span>{record.contactInfo || record.phoneNumber || record.phone}</span>
            </Space>
          )}
          {record.email && (
            <Space>
              <MailOutlined />
              <span>{record.email}</span>
            </Space>
          )}
        </Space>
      )
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.location ? (
            <>
              <Space>
                <EnvironmentOutlined />
                <span>{record.location}</span>
              </Space>
              {record.lastUpdated && (
                <Space>
                  <ClockCircleOutlined />
                  <small style={{ color: '#666' }}>
                    {record.lastUpdated.toDate().toLocaleString()}
                  </small>
                </Space>
              )}
            </>
          ) : (
            <span style={{ color: '#999', fontStyle: 'italic' }}>No location set</span>
          )}
        </Space>
      )
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes) => notes || <span style={{ color: '#999', fontStyle: 'italic' }}>No notes</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedStaff(record);
            setIsModalVisible(true);
          }}
        >
          Update Location
        </Button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading staff status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="status-container">
      {/* Crisis Summary Block */}
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>🤖 Crisis Summary</span>
                    <Button
                      type="primary"
                      size="small"
              icon={<ReloadOutlined />}
              loading={summaryLoading}
              onClick={handleRefreshSummary}
                      style={{ 
                        backgroundColor: '#39325b', 
                        borderColor: '#39325b',
                color: 'white',
                        fontSize: '12px'
                      }}
                    >
              Refresh Analysis
                    </Button>
              </div>
            }
        style={{ marginBottom: '24px' }}
      >
            <div style={{ 
              padding: '16px', 
          width: '90%',
          margin: '0 auto'
        }}>
          {summaryLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin size="large" />
              <div style={{ marginTop: '16px', color: '#666', fontSize: '16px' }}>
                Summarising crisis data
                  </div>
                </div>
          ) : (
            formatSummaryHtml(executiveSummary)
          )}
                  </div>
      </Card>

      {/* Two Column Layout - Staff Status and Site Status */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        
        {/* Staff Status Column */}
        <div style={{ border: '1px solid #e8e8e8', borderRadius: '8px', padding: '16px' }}>
          <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>
            Staff Status Overview
            <Tooltip title="Staff status for this specific incident only">
              <InfoCircleOutlined style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }} />
                </Tooltip>
          </h3>
                    <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
            gap: '12px'
          }}>
            {statusOptions.map(option => (
              <Card 
                key={option.value}
                hoverable
                onClick={() => handleBadgeClick(option.value, 'staff')}
                        style={{ 
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  minWidth: '160px',
                  minHeight: '50px',
                  position: 'relative'
                }}
                bodyStyle={{ 
                  padding: '8px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {/* Manual Add Button for Hospital and Deceased */}
                {(option.value === 'hospitalised' || option.value === 'deceased') && (
                      <Button
                    type="text"
                        size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddStaffClick(option.value);
                    }}
                        style={{ 
                      position: 'absolute',
                      top: '2px',
                      right: '0px',
                      padding: '1px 2px',
                      fontSize: '9px',
                      height: 'auto',
                      border: 'none',
                      color: option.countColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      background: 'rgba(255,255,255,0.9)',
                      borderRadius: '3px'
                    }}
                  >
                    <PlusOutlined style={{ fontSize: '7px' }} />
                    Add
                      </Button>
                )}
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>
                    {option.icon}
                  </span>
                  <span style={{ 
                          fontWeight: 'bold',
                    color: '#333', 
                    fontSize: '12px',
                    marginLeft: (option.value === 'hospitalised' || option.value === 'deceased') ? '2px' : '0px'
                        }}>
                    {option.label}
                  </span>
                        </div>
                <span style={{ 
                          fontWeight: 'bold',
                  color: option.countColor,
                  fontSize: '16px'
                }}>
                  {incidentStaffMetrics[option.value] || 0}
                </span>
                    </Card>
                          ))}
                        </div>
                              </div>

        {/* Site Status Column */}
        <div style={{ border: '1px solid #e8e8e8', borderRadius: '8px', padding: '16px' }}>
          <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>
            Site Status Overview
            <Tooltip title="Site status for this specific incident only">
              <InfoCircleOutlined style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }} />
                    </Tooltip>
          </h3>
        <div style={{ 
          display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
          gap: '12px'
          }}>
            {siteStatusOptions.map(option => (
                <Card 
                key={option.value}
                  hoverable
                onClick={(e) => handleSiteBadgeClick(e, option.value)}
                  style={{ 
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                    cursor: 'pointer',
                  transition: 'transform 0.2s',
                  minWidth: '160px',
                  minHeight: '50px'
                }}
                bodyStyle={{ 
                                padding: '8px 12px',
                        display: 'flex', 
                        justifyContent: 'space-between', 
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '14px' }}>
                        {option.icon}
                      </span>
                  <span style={{ fontWeight: 'bold', color: '#333', fontSize: '12px' }}>
                    {option.label}
                  </span>
            </div>
                <span style={{ 
                  fontWeight: 'bold', 
                  color: option.countColor,
                  fontSize: '16px'
                }}>
                  {siteMetrics[option.value] || 0}
                </span>
                    </Card>
          ))}
        </div>
                        </div>
      </div>

      {/* Dashboard-style Badge Modal */}
      <Modal
        title={(() => {
          if (badgeModalType === 'staff') {
            return `${statusOptions.find(opt => opt.value === selectedBadgeFilter)?.icon} ${statusOptions.find(opt => opt.value === selectedBadgeFilter)?.label} Staff Details`;
          } else if (badgeModalType === 'site') {
            if (selectedBadgeFilter === 'all') {
              return 'All Sites - Search and Browse';
            }
            return `${siteStatusOptions.find(opt => opt.value === selectedBadgeFilter)?.icon} ${siteStatusOptions.find(opt => opt.value === selectedBadgeFilter)?.label} Sites Details`;
          }
          return 'Details';
        })()}
        open={showBadgeModal}
        onCancel={() => {
          setShowBadgeModal(false);
        }}
        footer={null}
        width={800}
      >
        {badgeModalType === 'staff' && (
            <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
            </div>
            ) : (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '16px' }}>
                    {staff.filter(s => s.status === selectedBadgeFilter).length} staff member{staff.filter(s => s.status === selectedBadgeFilter).length !== 1 ? 's' : ''} with status: {selectedBadgeFilter}
                  </Text>
          </div>
                <Table
                  dataSource={staff.filter(s => s.status === selectedBadgeFilter)}
                  rowKey="id"
                  pagination={false}
              size="small" 
          columns={[
            {
              title: 'Name',
              key: 'name',
              width: '25%',
              render: (_, record) => record.fullName || record.name || 'Unknown'
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: '20%',
              render: (status, record) => (
                <Select
                  value={status}
                  onChange={(newStatus) => handleStatusChange(record.id, newStatus)}
                  style={{ width: '100%' }}
                  size="small"
                >
                  {statusOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px' }}>{option.icon}</span>
                        <span>{option.label}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              )
            },
            {
              title: 'Location',
              dataIndex: 'location',
              key: 'location',
                      width: '20%'
            },
            {
                      title: 'Contact Info',
              key: 'contactInfo',
                      width: '20%',
              render: (_, record) => (
                <div>
                  {record.email && (
                    <div style={{ fontSize: '12px', marginBottom: '2px' }}>
                      📧 {record.email}
                    </div>
                  )}
                  {(record.contactInfo || record.phoneNumber || record.phone) && (
                    <div style={{ fontSize: '12px' }}>
                      📞 {record.contactInfo || record.phoneNumber || record.phone}
                    </div>
                  )}
                  {!record.email && !record.contactInfo && !record.phoneNumber && !record.phone && (
                    <span style={{ color: '#999', fontSize: '12px' }}>No contact info</span>
                  )}
                </div>
              )
            },
            {
              title: 'Last Updated',
              dataIndex: 'lastUpdated',
              key: 'lastUpdated',
                      width: '20%',
              render: (timestamp) => {
                        if (!timestamp) return 'N/A';
                const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
                return date.toLocaleString();
              }
                    }
                  ]}
                />
            </div>
          )}
        </div>
        )}

        {badgeModalType === 'site' && (
          <div>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Text>Site details are managed at the organization level. This incident-specific view focuses on staff status tracking.</Text>
      </div>
          </div>
        )}

      </Modal>

      {/* Location Update Modal */}
      <Modal
        title="Update Staff Location"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleLocationUpdate}
          initialValues={{
            location: selectedStaff?.location || '',
            notes: selectedStaff?.notes || ''
          }}
        >
          <Form.Item
            label="Location"
            name="location"
            rules={[{ required: true, message: 'Please enter a location' }]}
          >
            <Input placeholder="Enter current location" />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <TextArea 
              rows={3} 
              placeholder="Additional notes (optional)" 
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
              Update Location
            </Button>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Staff Modal */}
      <Modal
        title={`Add ${addStaffType === 'hospitalised' ? 'Hospitalised' : 'Deceased'} Staff Member`}
        open={addStaffModalVisible}
        onCancel={() => {
          setAddStaffModalVisible(false);
          addStaffForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={addStaffForm}
          layout="vertical"
          onFinish={handleAddStaffSubmit}
        >
          <Form.Item
            label="Full Name"
            name="fullName"
            rules={[{ required: true, message: 'Please enter the full name' }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>

          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input placeholder="Enter email address (optional)" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phone"
          >
            <Input placeholder="Enter phone number (optional)" />
          </Form.Item>

          <Form.Item
            label="Staff ID or Internal Reference"
            name="staffId"
          >
            <Input placeholder="Optional: Enter staff ID or reference" />
          </Form.Item>

          <Form.Item
            label="Location / Site"
            name="location"
            rules={[{ required: true, message: 'Please enter location' }]}
          >
            <Input placeholder="Enter location or site" />
          </Form.Item>

          <Form.Item
            label="Incident Role"
            name="incidentRole"
          >
            <Select placeholder="Select incident role (optional)">
              <Select.Option value="Site Lead">Site Lead</Select.Option>
              <Select.Option value="Nurse">Nurse</Select.Option>
              <Select.Option value="Security">Security</Select.Option>
              <Select.Option value="Visitor">Visitor</Select.Option>
              <Select.Option value="Contractor">Contractor</Select.Option>
              <Select.Option value="Emergency Services">Emergency Services</Select.Option>
              <Select.Option value="Other">Other</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Date/Time of Status Change"
            name="statusChangeTime"
            rules={[{ required: true, message: 'Please select date and time' }]}
          >
            <DatePicker 
              showTime 
              format="DD/MM/YYYY HH:mm" 
              style={{ width: '100%' }}
              placeholder="Select date and time of status change"
            />
          </Form.Item>

          <Form.Item
            label="Optional Notes"
            name="notes"
          >
            <TextArea 
              rows={3} 
              placeholder="Enter any additional context or notes"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Add Staff Member
              </Button>
              <Button onClick={() => {
                setAddStaffModalVisible(false);
                addStaffForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Live Update Confirmation Modal for Deceased */}
      <Modal
        title="Confirm Auto-Live Update"
        open={liveUpdateModalVisible}
        onCancel={() => {
          setLiveUpdateModalVisible(false);
          liveUpdateForm.resetFields();
          setDeceasedStaffData(null);
        }}
        footer={null}
        width={700}
      >
        <p style={{ marginBottom: '16px' }}>
          A Live Update message will be posted to notify relevant teams of the deceased status. 
          Please review and confirm.
        </p>

        <Form
          form={liveUpdateForm}
          layout="vertical"
          onFinish={handleLiveUpdateSubmit}
          initialValues={{
            liveUpdateMessage: deceasedStaffData ? 
              `Critical Update: A staff member has been reported as deceased - for CMT member awareness and action as appropriate. More details are contained in the relevant Staff Status popup.` 
              : ''
          }}
        >
          <Form.Item
            label="Live Update Message"
            name="liveUpdateMessage"
            rules={[{ required: true, message: 'Please enter the update message' }]}
          >
            <TextArea 
              rows={6} 
              placeholder="Enter the live update message"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Post Update
            </Button>
              <Button type="default" onClick={() => {
                // Just close without posting update
                setLiveUpdateModalVisible(false);
                liveUpdateForm.resetFields();
                setDeceasedStaffData(null);
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Site Status Modal (replicated from Dashboard) */}
      <Modal
        title="Site Status for This Incident"
        open={showSiteModal}
        onCancel={() => setShowSiteModal(false)}
        footer={null}
        width={600}
      >
        {siteMetrics.loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div>
            {/* Prominent Total */}
            <div style={{ textAlign: 'center', marginBottom: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#39325b', marginBottom: '4px' }}>
                {siteMetrics.active + siteMetrics.degraded + siteMetrics.evacuating + siteMetrics.evacuated + siteMetrics.closed_and_secure + siteMetrics.unknown}
              </div>
              <Text strong style={{ fontSize: '14px', color: '#666' }}>Total Sites</Text>
            </div>
            
            {/* Visual Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🟢</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a', marginBottom: '2px' }}>{siteMetrics.active}</div>
                <div style={{ fontSize: '12px', color: '#52c41a' }}>Active</div>
              </div>
              
              <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>⚠️</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16', marginBottom: '2px' }}>{siteMetrics.degraded}</div>
                <div style={{ fontSize: '12px', color: '#fa8c16' }}>Degraded</div>
                        </div>
              
              <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fff1f0', borderRadius: '6px', border: '1px solid #ffa39e' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🚨</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f5222d', marginBottom: '2px' }}>{siteMetrics.evacuating}</div>
                <div style={{ fontSize: '12px', color: '#f5222d' }}>Evacuating</div>
                            </div>
              
              <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f9f0ff', borderRadius: '6px', border: '1px solid #d3adf7' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🚪</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#722ed1', marginBottom: '2px' }}>{siteMetrics.evacuated}</div>
                <div style={{ fontSize: '12px', color: '#722ed1' }}>Evacuated</div>
                            </div>
              
              <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🔒</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff', marginBottom: '2px' }}>{siteMetrics.closed_and_secure}</div>
                <div style={{ fontSize: '12px', color: '#1890ff' }}>Closed and Secure</div>
                          </div>
              
              <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>❓</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#8c8c8c', marginBottom: '2px' }}>{siteMetrics.unknown}</div>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Unknown</div>
                        </div>
                        </div>
            
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Live site status for this specific incident. Visit the Map tab for detailed site locations and individual site management.
            </Text>
              </div>
            )}
      </Modal>

      {/* Site Badge Details Modal (replicated from Dashboard) */}
      <Modal
        title={`Site Status: ${selectedSiteBadgeFilter ? selectedSiteBadgeFilter.charAt(0).toUpperCase() + selectedSiteBadgeFilter.slice(1) : ''} Sites`}
        open={showSiteBadgeModal}
        onCancel={() => {
          setShowSiteBadgeModal(false);
          setSiteSearchTerm(''); // Reset search when closing
        }}
        footer={null}
        width={800}
      >
        {detailedSiteData.loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
        </div>
        ) : (() => {
          // Filter sites by search term
          const filteredSites = detailedSiteData.sites.filter(site => {
            const search = siteSearchTerm.toLowerCase();
            return (
              site.site_name?.toLowerCase().includes(search) ||
              site.site_type?.toLowerCase().includes(search) ||
              site.site_manager_name?.toLowerCase().includes(search) ||
              // Address fields
              site.address_line_1?.toLowerCase().includes(search) ||
              site.address_line_2?.toLowerCase().includes(search) ||
              site.suburb?.toLowerCase().includes(search) ||
              site.state?.toLowerCase().includes(search) ||
              site.postcode?.toLowerCase().includes(search) ||
              // Status
              site.status?.toLowerCase().includes(search) ||
              // Contact info
              site.site_manager_mobile?.toLowerCase().includes(search) ||
              site.site_manager_email?.toLowerCase().includes(search)
            );
          });

          return (
            <div>
              {/* Search input */}
              <div style={{ marginBottom: '16px' }}>
                <Input
                  placeholder="Search sites by name, type, manager, address, status..."
                  prefix={<SearchOutlined />}
                  value={siteSearchTerm}
                  onChange={(e) => setSiteSearchTerm(e.target.value)}
                  suffix={
                    siteSearchTerm && (
                      <Button
                        type="text"
                        size="small"
                        icon={<ClearOutlined />}
                        onClick={clearSiteSearch}
                        style={{ padding: '0 4px' }}
                      />
                    )
                  }
                  style={{ marginBottom: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '16px' }}>
                  {filteredSites.length} site{filteredSites.length !== 1 ? 's' : ''} 
                  {` with status: ${selectedSiteBadgeFilter}`}
                  {siteSearchTerm && (
                    <Text type="secondary" style={{ marginLeft: '8px' }}>
                      matching "{siteSearchTerm}"
                    </Text>
                  )}
                </Text>
              </div>
        <Table
                dataSource={filteredSites}
                rowKey="id"
                pagination={false}
                size="small"
          columns={[
            {
                    title: 'Site Name',
                    dataIndex: 'site_name',
                    key: 'site_name',
                    width: '25%'
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
                    width: '15%',
                    render: (status) => (
                      <Tag color={
                        status === 'active' ? 'green' :
                        status === 'degraded' ? 'orange' :
                        status === 'evacuating' ? 'red' :
                        status === 'evacuated' ? 'purple' :
                        status === 'closed_and_secure' ? 'blue' :
                        'default'
                      }>
                        {status}
                      </Tag>
                    )
                  },
                  {
                    title: 'Type',
                    dataIndex: 'site_type',
                    key: 'site_type',
                    width: '15%'
                  },
                  {
                    title: 'Address',
                    dataIndex: 'address_line_1',
                    key: 'address_line_1',
                    width: '30%'
                  },
                  {
                    title: 'Manager',
                    dataIndex: 'site_manager_name',
                    key: 'site_manager_name',
                    width: '15%'
                  }
                ]}
              />
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default StatusTab;